/**
 * @module Compiling
 * @author Jacob Viertel <jv@onscreen.net>
 * @document ../doc/compile.md
 */

import {isDefined} from "@acng/frontend-bounty/types";
import {TAGNAME_TEMPLATE, isCommentNode, isElementNode, isTextNode} from "@acng/frontend-bounty/dom/type.js";
import {before, cloneNode, createText, removeNode, whenAll} from "@acng/frontend-bounty";
import {pushToArray} from "@acng/frontend-bounty/array";
import {parseJSON} from "@acng/frontend-bounty/parse";
import {STRING, typeguard} from "@acng/frontend-bounty/typeguard.js";
import {parentElement, replace as replaceNode} from "@acng/frontend-bounty";
import {create} from "@acng/frontend-bounty/object.js";
import {WeakStore} from "@acng/frontend-bounty/collection.js";

import {REGISTRY_ELEMENT} from "../model/resource.js";
import {element, nodes, precursorFlag, render, scope} from "./render.js";
import {rootNamespace} from "./root.js";
import {lookup} from "./lookup.js";
import {setupAttribute, setupElement} from "./setup.js";

const MODULE = "stargazer/compile";

const REGEXP = /([#$]){([^}]*)}({)?/g;
const REGEXP_JSON = /({.*?})#/g;
//const REGEXP_DIR = /#{([^}]*)}/g;
const REGEXP_ID = /(\w[\w-]*)/g;

/**
 * @template {keyof HTMLElementTagNameMap} K
 * @overload
 * @param {K} name
 * @param {import("./types").CompileElementFn<HTMLElementTagNameMap[K]>} fn
 * @returns {void}
 */

/**
 * @overload
 * @param {string} name
 * @param {import("./types").CompileElementFn} compiler
 * @returns {void}
 */

/**
 * @param {string} name
 * @param {import("./types").CompileElementFn} compiler
 * @returns {void}
 */
export function defineCompileElement(name, compiler) {
  ASSERT: if (isDefined(compileElements[name])) throw Error(`${name} is already defined`);

  compileElements[name] = compiler;
}

/**
 * @param {string} name
 * @param {import("./types").CompileAttrFn} compiler
 * @returns {void}
 */
export function defineCompileAttribute(name, compiler) {
  ASSERT: if (isDefined(compileAttrs[name])) throw Error(`${name} is already defined`);

  compileAttrs[name] = compiler;
}

/**
 * TODO mark the resource element with an attibute if there is
 * TODO nothing to render (nodes, params, controllers) data-render="NPC"
 * @internal
 */
export const compile = WeakStore(
  /**
   * @param {HTMLTemplateElement} resource
   * @returns {Promise<HTMLTemplateElement>}
   */
  async (resource) => {
    DEBUG: if (resource.hasAttribute("debug")) {
      performance.mark(`${MODULE}:started`);
      console.info(MODULE, {resource});
    }

    /** @type {Promise<unknown>[]} */
    const promises = [];
    compileChildNodes(resource, promises, resource.content);
    await whenAll(promises);

    DEBUG: if (resource.hasAttribute("debug")) {
      console.info(`${MODULE} compiled`, {
        duration: performance.measure(MODULE, `${MODULE}:started`).duration,
      });
    }

    return resource;
  }
);

/**
 * @param {HTMLTemplateElement} resource
 * @param {Promise<unknown>[]} promises
 * @param {ParentNode} node
 * @ŧodo TODO detect and break loops
 */
const compileChildNodes = (resource, promises, node) => {
  for (let i = node.childNodes.length - 1; i >= 0; i--) {
    const childNode = node.childNodes[i];

    if (isTextNode(childNode)) {
      compileTextNode(resource, childNode, promises);
    } else if (isElementNode(childNode)) {
      compileElement(resource, childNode, promises, node);
    } else if (isCommentNode(childNode)) {
      removeNode(childNode);
    }
  }
};

/**
 * @param {HTMLTemplateElement} resource
 * @param {Text} node
 * @param {Promise<unknown>[]} promises
 */
const compileTextNode = (resource, node, promises) => {
  const text = node.nodeValue;

  // Typescript thinks that nodeValue of Text might be null.
  ASSERT: typeguard(`${MODULE} Text.nodeValue`, text, STRING);

  /** @type {RegExpExecArray | null} */
  let match;
  let lastMatch = 0;
  REGEXP.lastIndex = 0;
  while ((match = REGEXP.exec(text))) {
    const [, prefix, dir, suffix] = match;
    /** @type {import("./types").Scope | undefined} */
    let params;
    if (match.index > lastMatch) {
      before(node, text.substring(lastMatch, match.index));
    }
    if (suffix) {
      REGEXP_JSON.lastIndex = REGEXP.lastIndex - 1;
      match = REGEXP_JSON.exec(text);
      if (match) {
        params = parseParameters(match[1]);
        REGEXP.lastIndex = REGEXP_JSON.lastIndex;
      }
    }
    lastMatch = REGEXP.lastIndex;
    if (prefix != "#") {
      const variable = parseValue(dir);
      if (variable) {
        // resource[RESOURCE_TEXT] = true; TODO maybe add data-attribute to resource
        before(node, variable);
      }
      continue;
    }
    const asyncReplace = createText();
    const path = parseDirective(dir);
    before(node, asyncReplace);
    pushToArray(promises, replace(resource, asyncReplace, path, params));
    REGEXP.lastIndex = lastMatch;
  }
  if (lastMatch < text.length) {
    before(node, text.substring(lastMatch));
  }
  removeNode(node);
};

/**
 * @param {string} json
 * @returns {import("./types").Scope | undefined}
 */
const parseParameters = (json) => {
  try {
    const params = parseJSON(json);
    return params;
  } catch (reason) {
    DEBUG: console.error(`${MODULE} ignoring parameters with syntax error`, {reason, json});
  }
};

/**
 * @param {string} data
 */
const parseValue = (data) => {
  REGEXP_ID.lastIndex = 0;
  const match = REGEXP_ID.exec(data);
  if (match) {
    return `\${${match[1]}}`;
  }
};

//const REGEXP_UP = /\.(\.)?\//g;

/**
 * @param {string} data
 *
 * TODO add base param to allow relative directive. add origin-url as symbol property to each template?
 * TODO detect loader type and rearrange to path array
 */
const parseDirective = (data) => {
  /** @type {RegExpExecArray | null} */
  let match;
  REGEXP_ID.lastIndex = 0;
  /*
  REGEXP_UP.lastIndex = 0;
  while ((match = REGEXP_UP.exec(data))) {
    if (match[1]) {
      dir = sliceOfArray(dir, 0, -1);
    }
    REGEXP_ID.lastIndex = REGEXP_UP.lastIndex;
  }
  if (REGEXP_ID.lastIndex == 0) {
    dir = [];
  }
  */
  /**
   * @type {string[]}
   */
  const dir = [];
  while ((match = REGEXP_ID.exec(data))) {
    pushToArray(dir, match[1]);
  }
  return dir;
};

/**
 * @todo
 * - {@link CompileFn} priorities
 *
 * @param {HTMLTemplateElement} resource
 * @param {Element} element
 * @param {Promise<unknown>[]} promises
 * @param {ParentNode} parentNode - Required to conditionally skip the subtree.
 */
const compileElement = (resource, element, promises, parentNode) => {
  const {attributes} = element;

  // Compile attributes. (a.t.m. only required to remove angularjs directives of inactive features)
  // In future there might be "compile time controllers", :if or :include or :require
  for (let i = attributes.length - 1; i >= 0; i--) {
    const {name, value} = attributes[i];

    setupAttribute(name);

    const compileAttr = compileAttrs[name];

    if (compileAttr) {
      compileAttr(element, value, promises);

      if (element.parentNode !== parentNode) {
        return;
      }
    }

    REGEXP.lastIndex = 0;
    let match = REGEXP.exec(value);
    if (match) {
      const [, prefix, dir, suffix] = match;
      /** @type {import("./types").Scope | undefined} */
      let params;
      if (prefix == "#") {
        if (suffix) {
          REGEXP_JSON.lastIndex = REGEXP.lastIndex - 1;
          match = REGEXP_JSON.exec(value);
          if (match) {
            params = parseParameters(match[1]);
            REGEXP.lastIndex = REGEXP_JSON.lastIndex;
          }
        }
        pushToArray(promises, replaceAttributeValue(attributes[i], parseDirective(dir), params));
      } else {
        attributes[i].value = parseValue(dir) ?? value;
      }
    }
  }

  const {localName} = element;

  setupElement(localName);

  const compileName = compileElements[localName];
  if (compileName) {
    compileName(element, promises);

    if (element.parentNode !== parentNode) {
      return;
    }
  }

  compileChildNodes(resource, promises, element);
};

/**
 * @param {HTMLTemplateElement} resource
 * @param {ChildNode} childNode
 * @param {string[]} path
 *
 * @param {import("./types").Scope | undefined} replacements
 *
 * @returns {Promise<void>}
 */
const replace = async (resource, childNode, path, replacements) => {
  DEBUG: if (resource.hasAttribute("debug")) console.debug(`${MODULE}`, {resource, childNode});

  const childResource = await lookup(rootNamespace, path);

  const fragment = render({
    [REGISTRY_ELEMENT]: childResource,
    [scope]: replacements,
    [element]: parentElement(childNode) ?? resource,
    [nodes]: undefined,
    [precursorFlag]: true,
  });

  replaceNode(childNode, fragment);
};

/**
 * @param {Attr} attribute
 * @param {string[]} path
 * @param {import("./types").Scope} [params]
 * @returns {Promise<void>}
 */
const replaceAttributeValue = async (attribute, path, params) => {
  const template = await lookup(rootNamespace, path);

  attribute.value =
    (params
      ? render({
          [REGISTRY_ELEMENT]: template,
          [scope]: params,
          [nodes]: undefined,
        })
      : cloneNode(template.content)
    ).textContent ?? "";
};

/**
 * @type {Record<string, import("./types").CompileElementFn | undefined>}
 */
const compileElements = create();

/**
 * @type {Record<string, import("./types").CompileAttrFn | undefined>}
 */
const compileAttrs = create();

defineCompileElement(TAGNAME_TEMPLATE, (template, promises) => pushToArray(promises, compile(template)));
